import { DestinationEvent } from '@eventbrite/event-renderer';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React from 'react';
import { NearbyPOITagShape } from '../../../../types/AppProps';
import { PointOfInterest } from '../../PointOfInterestPage';
import { ResponsiveSection } from '../ResponsiveSection';
import { FAQItem, SectionData } from './FAQItem';
import styles from './faqs.module.scss';
import { FAQS_ENUM, getFAQs } from './utils/getFAQs';

export interface PointsOfInterestFAQProps {
    pointOfInterestName: PointOfInterest['pointOfInterestName'];
    hoursOfOperation: PointOfInterest['hoursOfOperation'];
    nearbyPointsOfInterest: Array<NearbyPOITagShape>;
    nearbyEvents: Array<DestinationEvent>;
}

type Question = {
    answerText: GenericLazyString;
    questionText: GenericLazyString;
    sectionData?: Array<SectionData>;
};

const FAQHeader = ({ text }: { text: GenericLazyString }) => {
    return (
        <h2 className={styles.header}>
            <p className="eds-text-hs">{text}</p>
        </h2>
    );
};

export const PointsOfInterestFAQs = ({
    pointOfInterestName,
    hoursOfOperation,
    nearbyPointsOfInterest,
    nearbyEvents,
}: PointsOfInterestFAQProps) => {
    const headerText = gettext(
        'Frequently asked questions about %(pointOfInterest)s',
        { pointOfInterest: pointOfInterestName },
    );

    const questions: Question[] = getFAQs({
        data: {
            hoursOfOperation,
            pointOfInterestName,
            nearbyPointsOfInterest,
            nearbyEvents,
        },
        faqs: [
            FAQS_ENUM.FAQ_HOURS_OF_OPERATION,
            FAQS_ENUM.FAQ_POPULAR_EVENTS,
            FAQS_ENUM.FAQ_NEARBY_POI,
        ],
    });

    const SHOW_FAQ_SECTION = questions.length > 0;

    return SHOW_FAQ_SECTION ? (
        <ResponsiveSection>
            <section
                className={styles.faqSection}
                itemScope
                itemType="https://schema.org/FAQPage"
            >
                <FAQHeader text={headerText} />
                <div className={styles.faqContainer}>
                    {questions.map(
                        ({ questionText, answerText, sectionData }) => (
                            <FAQItem
                                key={questionText.toString()}
                                questionText={questionText}
                                answerText={answerText}
                                sectionData={sectionData}
                            />
                        ),
                    )}
                </div>
            </section>
        </ResponsiveSection>
    ) : null;
};
